import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { getGraderOrders, confirmGraderReceipt } from '../services/api'
import { formatAddress } from '../services/wallet'
import OrderItem from '../components/OrderItem'
import Section from '../components/m1nt/Section'
import { M1ntButton } from '../components/m1nt/ui'
import { useBeginWalletConnect } from '../contexts/WalletConnectReminderContext'

export default function GraderDashboard() {
  const beginWalletConnect = useBeginWalletConnect()
  const { address } = useAccount()
  const wallet = address ?? null
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('in_transit')
  const [confirmingId, setConfirmingId] = useState(null)

  useEffect(() => {
    if (wallet) {
      loadData(wallet)
    } else {
      setLoading(false)
    }
  }, [wallet])

  const loadData = async (address) => {
    setLoading(true)
    try {
      // Get orders assigned to this grader (secured grader endpoint)
      const ordersRes = await getGraderOrders(address, { limit: 100 })
      setOrders(ordersRes.data.data || [])
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const inTransit = orders.filter((o) => o.status === 'SHIPPED_BY_OWNER')
  const inProgress = orders.filter((o) => ['CARD_RECEIVED', 'IN_PROGRESS'].includes(o.status))
  const completed = orders.filter((o) => o.status === 'COMPLETED' || o.completed)

  const filteredOrders = (() => {
    if (filter === 'in_transit') return inTransit
    if (filter === 'in_progress') return inProgress
    if (filter === 'completed') return completed
    return orders
  })()

  const myStats = {
    total: orders.length,
    inTransit: inTransit.length,
    inProgress: inProgress.length,
    completed: completed.length,
    earnings: orders.filter((o) => o.paid).reduce((sum, o) => sum + parseFloat(o.price || 0), 0),
  }

  if (!wallet) {
    return (
      <Section
        first
        eyebrow="Grader portal"
        title="Grader Dashboard"
        subtitle="Connect your wallet to access your grading dashboard."
        actions={
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <M1ntButton onClick={() => beginWalletConnect(openConnectModal)}>
                Connect Wallet
              </M1ntButton>
            )}
          </ConnectButton.Custom>
        }
      >
        <p className="max-w-[30em] text-[16px] font-light leading-relaxed text-m1-muted">
          Only wallets assigned as graders can see incoming orders and start inspections.
        </p>
      </Section>
    )
  }

  if (loading) {
    return (
      <Section first eyebrow="Grader portal" title="Grader Dashboard" subtitle="Loading dashboard…">
        <div
          className="h-8 w-8 rounded-full border border-m1-line border-t-m1-violet"
          style={{ animation: 'm1nt-spin 0.9s linear infinite' }}
          role="status"
          aria-label="Loading dashboard"
        />
      </Section>
    )
  }

  const statTiles = [
    { label: 'Total Orders', value: myStats.total },
    { label: 'In Transit', value: myStats.inTransit },
    { label: 'In Progress', value: myStats.inProgress },
    { label: 'Completed', value: myStats.completed },
    { label: 'Total Earned', value: `${myStats.earnings.toFixed(2)} MATIC` },
  ]

  const tabs = [
    { key: 'in_transit', label: 'In Transit', count: myStats.inTransit },
    { key: 'in_progress', label: 'In Progress', count: myStats.inProgress },
    { key: 'completed', label: 'Completed', count: myStats.completed },
  ]

  return (
    <>
      <Section
        first
        eyebrow="Grader portal"
        title="Grader Dashboard"
        subtitle={`Connected as ${formatAddress(wallet)}`}
      >
        <dl className="grid grid-cols-2 gap-x-8 gap-y-8 md:grid-cols-5">
          {statTiles.map((tile) => (
            <div key={tile.label} className="border-t border-m1-line pt-4">
              <dt className="text-[14px] text-m1-muted">{tile.label}</dt>
              <dd className="mt-3 font-m1-display text-[clamp(26px,3vw,36px)] font-normal leading-none text-m1-ink">
                {tile.value}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section eyebrow="Orders" title="Assigned orders">
        <div className="mb-10 flex flex-wrap gap-3" role="tablist" aria-label="Order filter">
          {tabs.map(({ key, label, count }) => {
            const selected = filter === key
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(key)}
                className={`inline-flex min-h-[44px] items-center gap-3 border px-5 text-[14px] tracking-[0.04em] transition-colors hover:border-m1-violet hover:text-m1-violet ${
                  selected ? 'border-m1-violet text-m1-violet' : 'border-white/30 text-m1-ink'
                }`}
              >
                {label}
                <span className="font-m1-mono text-[12px] text-m1-muted">{count}</span>
              </button>
            )
          })}
        </div>

        {filteredOrders.length > 0 ? (
          <div className="space-y-6">
            {filteredOrders.map((order) => (
              <div key={order.id} className="relative">
                <OrderItem order={order} role="grader" />
                {order.status === 'SHIPPED_BY_OWNER' && (
                  <div className="mt-3 flex gap-2">
                    <M1ntButton
                      disabled={confirmingId === order.id}
                      onClick={async () => {
                        const ok = window.confirm('Confirm you have physically received this card?')
                        if (!ok) return
                        setConfirmingId(order.id)
                        try {
                          await confirmGraderReceipt(wallet, order.id)
                          await loadData(wallet)
                        } finally {
                          setConfirmingId(null)
                        }
                      }}
                      className="border-m1-violet text-m1-violet"
                    >
                      <i className="ri-inbox-archive-line text-lg" aria-hidden="true" />
                      {confirmingId === order.id ? 'Confirming...' : 'Confirm Card Received'}
                    </M1ntButton>
                  </div>
                )}
                {['CARD_RECEIVED', 'IN_PROGRESS'].includes(order.status) && (
                  <div className="mt-3">
                    <M1ntButton
                      as={Link}
                      to={`/grader/inspect/${order.id}`}
                      className="border-m1-violet text-m1-violet"
                    >
                      <i className="ri-camera-line text-lg" aria-hidden="true" />
                      Start / Continue Inspection
                    </M1ntButton>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-m1-line px-6 py-14 text-center">
            <h3 className="font-m1-display text-[28px] font-normal text-m1-ink">No orders found</h3>
            <p className="mx-auto mt-3 max-w-[28em] text-[16px] font-light leading-relaxed text-m1-muted">
              No orders match the selected filter.
            </p>
          </div>
        )}
      </Section>
    </>
  )
}
